import { X, ExternalLink, Play } from "lucide-react";
import { AppIcon } from "@/components/ui/app-icon";
import { getYouTubeVideoId } from "@/lib/utils";
import type { ComposeLinkPreview } from "@/hooks/useLinkPreview";

/**
 * Compose-time link preview card. Shared by FeedComposer and ComposeModal so the
 * two stay in sync. For YouTube URLs it renders a static 16:9 video card
 * (thumbnail + play badge + title) that mirrors the feed's embed — but is NOT
 * playable in the composer (no autoplay, no navigation). All other links render
 * the standard Open Graph card.
 */
export default function ComposeLinkPreviewCard({
  preview,
  onDismiss,
}: {
  preview: ComposeLinkPreview;
  onDismiss: () => void;
}) {
  const youtubeId = getYouTubeVideoId(preview.url);

  const siteLabel =
    preview.siteName ||
    (() => {
      try {
        return new URL(preview.url).hostname.replace(/^www\./, "");
      } catch {
        return preview.url;
      }
    })();

  return (
    <div className="relative mt-2 rounded-xl border border-(--scroll-border) overflow-hidden">
      {youtubeId ? (
        <div className="relative w-full aspect-video bg-muted">
          <img
            src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
            alt={preview.title || ""}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white translate-x-0.5" />
            </div>
          </div>
        </div>
      ) : (
        preview.imageUrl && (
          <div className="w-full aspect-2/1 bg-muted overflow-hidden">
            <img
              src={preview.imageUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                const container = (e.target as HTMLImageElement).parentElement;
                if (container) container.style.display = "none";
              }}
            />
          </div>
        )
      )}
      <div className="px-3 py-2">
        <div className="flex items-center gap-1.5 mb-0.5">
          {preview.favicon && (
            <img
              src={preview.favicon}
              alt=""
              className="w-3.5 h-3.5 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span className="text-xs text-(--sl-text-tertiary)">{siteLabel}</span>
          <AppIcon icon={ExternalLink} size="xs" className="text-(--sl-text-tertiary)" />
        </div>
        {preview.title && (
          <p className="text-sm font-semibold text-(--sl-text) line-clamp-1">{preview.title}</p>
        )}
        {preview.description && (
          <p className="text-xs text-(--sl-text-secondary) line-clamp-2 mt-0.5">
            {preview.description}
          </p>
        )}
      </div>
      <button
        type="button"
        className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors"
        onClick={onDismiss}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
